// 選択状態管理関連のハンドラー
import { getSettings } from '../state/app-state.js';
import { getImportedApps } from '../state/app-state.js';
import { createAppSection } from '../components/app-importer.js';
import { t } from '../utils/i18n.js';

/**
 * 選択された設定数を更新
 */
export function updateSelectedCount(): void {
    const currentSettings = getSettings();
    if (!currentSettings) return;
    let count = 0;
    const appSection = createAppSection(getImportedApps());
    const allSections = appSection && appSection.sections[0] 
        ? [appSection.sections[0], ...(currentSettings.sections || [])] 
        : (currentSettings.sections || []);
    
    allSections.forEach((section: any) => {
        section.items.forEach((item: any) => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox && checkbox.checked) {
                count++;
            }
        });
    });
    const countContainer = document.getElementById('selectedCount');
    if (countContainer) {
        const labelSpan = countContainer.querySelector('span');
        if (labelSpan) {
            labelSpan.textContent = t('selectedCount', { count });
        }
    }
    
    // セクションごとの選択数を更新（折りたたみヘッダーのバッジ）
    updateSectionBadges();
}

/**
 * 全てのチェックを外す
 */
export function clearAllSelections(): void {
    const currentSettings = getSettings();
    if (!currentSettings) return;

    // 設定セクション側のチェックを全て外す
    if (currentSettings.sections) {
        currentSettings.sections.forEach(section => {
            section.items.forEach(item => {
                const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
                if (checkbox) {
                    checkbox.checked = false;
                }
            });
        });
    }

    // アプリケーションセクション側のチェックも全て外す
    const appSection = createAppSection(getImportedApps());
    if (appSection && appSection.sections[0]) {
        appSection.sections[0].items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox) {
                checkbox.checked = false;
            }
        });
    }

    // コードと選択数・各種トグル状態を再計算（ハイライトは不要）
    if (window.app && window.app.updateCode) {
        window.app.updateCode(false);
    }
}

/**
 * セクションごとの選択数バッジを更新
 */
function updateSectionBadges(): void {
    const currentSettings = getSettings();
    if (!currentSettings) return;
    // 大項目（カテゴリ）のバッジを更新
    if (currentSettings.categories) {
        currentSettings.categories.forEach((category, categoryIndex) => {
            let categorySelectedCount = 0;
            category.sections.forEach(section => {
                section.items.forEach(item => {
                    const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
                    if (checkbox && checkbox.checked) {
                        categorySelectedCount++;
                    }
                });
            });
            
            const categoryId = 'category_' + categoryIndex;
            const categoryHeader = document.querySelector(`[onclick="window.app.toggleCategory('${categoryId}')"]`);
            if (categoryHeader) {
                const existingBadge = categoryHeader.querySelector('.bg-purple-600');
                if (categorySelectedCount > 0) {
                    if (existingBadge) {
                        existingBadge.textContent = String(categorySelectedCount);
                    } else {
                        const badge = document.createElement('span');
                        badge.className = 'text-xs bg-purple-600 text-white px-2 py-0.5 rounded ml-2';
                        badge.textContent = String(categorySelectedCount);
                        const titleElement = categoryHeader.querySelector('h1');
                        if (titleElement && titleElement.parentElement) {
                            titleElement.parentElement.appendChild(badge);
                        }
                    }
                } else {
                    if (existingBadge) {
                        existingBadge.remove();
                    }
                }
            }
        });
    }
    
    // 中項目（セクション）のバッジを更新
    if (currentSettings.categories) {
        currentSettings.categories.forEach((category, categoryIndex) => {
            const categoryId = 'category_' + categoryIndex;
            category.sections.forEach((section, sectionIndex) => {
                const selectedCount = section.items.filter(item => {
                    const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
                    return checkbox && checkbox.checked;
                }).length;
                
                // カテゴリインデックスとセクションインデックスを含めて一意なセクションIDを生成
                const sectionId = categoryId + '_section_' + sectionIndex;
                const header = document.querySelector(`[onclick="window.app.toggleSection('${sectionId}')"]`);
                if (header) {
                    const existingBadge = header.querySelector('.bg-blue-600');
                    if (selectedCount > 0) {
                        if (existingBadge) {
                            existingBadge.textContent = String(selectedCount);
                        } else {
                            const badge = document.createElement('span');
                            badge.className = 'text-xs bg-blue-600 text-white px-2 py-0.5 rounded ml-2';
                            badge.textContent = String(selectedCount);
                            const titleElement = header.querySelector('h2');
                            if (titleElement && titleElement.parentElement) {
                                titleElement.parentElement.appendChild(badge);
                            }
                        }
                    } else {
                        if (existingBadge) {
                            existingBadge.remove();
                        }
                    }
                }
            });
        });
    }
    
    // アプリケーションセクションのバッジを更新
    const appSection = createAppSection(getImportedApps());
    if (appSection && appSection.sections[0]) {
        const selectedCount = appSection.sections[0].items.filter(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            return checkbox && checkbox.checked;
        }).length;
        
        const sectionId = 'app_category_section_0';
        const header = document.querySelector(`[onclick="window.app.toggleSection('${sectionId}')"]`);
        if (header) {
            const existingBadge = header.querySelector('.bg-blue-600');
            if (selectedCount > 0) {
                if (existingBadge) {
                    existingBadge.textContent = String(selectedCount);
                } else {
                    const badge = document.createElement('span');
                    badge.className = 'text-xs bg-blue-600 text-white px-2 py-0.5 rounded ml-2';
                    badge.textContent = String(selectedCount);
                    const titleElement = header.querySelector('h2');
                    if (titleElement && titleElement.parentElement) {
                        titleElement.parentElement.appendChild(badge);
                    }
                }
            } else {
                if (existingBadge) {
                    existingBadge.remove();
                }
            }
        }
    }
}

/**
 * 管理者権限が必要な設定が有効かチェック
 */
function checkAdminRequirements(): boolean {
    const currentSettings = getSettings();
    if (!currentSettings || !currentSettings.sections) return false;
    let needsAdmin = false;
    currentSettings.sections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (item.requiresAdmin && checkbox && checkbox.checked) {
                needsAdmin = true;
            }
        });
    });
    // アプリケーションセクションもチェック
    const appSection = createAppSection(getImportedApps());
    if (appSection && appSection.sections && appSection.sections[0] && appSection.sections[0].items.length > 0) {
        const hasSelectedApps = appSection.sections[0].items.some(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            return checkbox && checkbox.checked;
        });
        if (hasSelectedApps) {
            needsAdmin = true;
        }
    }
    return needsAdmin;
}

/**
 * 管理者権限トグルの状態を更新（警告表示 + 自動チェック）
 */
export function updateAdminToggleState(): void {
    const adminToggle = document.getElementById('toggleAdmin') as HTMLInputElement | null;
    const warningIcon = document.getElementById('adminWarningIcon');
    if (!adminToggle || !warningIcon) return;
    
    const needsAdmin = checkAdminRequirements();
    // 管理者権限が必要な設定が有効な場合、自動的にチェックを入れる
    if (needsAdmin && !adminToggle.checked) {
        adminToggle.checked = true;
        // チェック状態が変わったので、コードも更新（ハイライトなし）
        // 循環参照を避けるため、window.app経由で呼び出す
        setTimeout(() => {
            if (window.app && window.app.updateCode) {
                window.app.updateCode(false);
            }
        }, 0);
    }
    
    // 警告表示は不要になったので削除（自動チェックされるため）
    warningIcon.classList.add('hidden');
    const container = document.getElementById('toggleContainer_toggleAdmin');
    if (container) {
        container.classList.remove('border-yellow-500', 'border-2');
    }
}

/**
 * 再起動が必要な設定が有効かチェック
 */
function checkRebootRequirements(): boolean {
    const currentSettings = getSettings();
    if (!currentSettings || !currentSettings.sections) return false;
    let needsReboot = false;
    currentSettings.sections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if ((item as any).requiresReboot && checkbox && checkbox.checked) {
                needsReboot = true;
            }
        });
    });
    return needsReboot;
}

/**
 * 再起動トグルの状態を更新（自動チェック）
 */
export function updateRebootToggleState(): void {
    const rebootToggle = document.getElementById('exec_reboot') as HTMLInputElement | null;
    if (!rebootToggle) return;

    const needsReboot = checkRebootRequirements();
    if (needsReboot && !rebootToggle.checked) {
        rebootToggle.checked = true;
        // チェック状態が変わったので、コードも更新（ハイライトなし）
        // 循環参照を避けるため、window.app経由で呼び出す
        setTimeout(() => {
            if (window.app && window.app.updateCode) {
                window.app.updateCode(false);
            }
        }, 0);
    }
}

